import React, { useCallback, useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Stack,
  Card,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { DndContext, rectIntersection, pointerWithin, DragOverlay, CollisionDetection } from '@dnd-kit/core';
import { snapCenterToCursor } from '@dnd-kit/modifiers';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useConfirm } from 'material-ui-confirm';

import TakeoverLayout from '_lex/_layouts/TakeoverLayout';
import { translate as t } from 'hooks/useTranslations';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import ActionMenu from 'components/_atoms/ActionMenu';

// Enable the relativeTime plugin for dayjs
dayjs.extend(relativeTime);
import { useTemplateBuilderContext } from './context';
import { Field } from 'views/App/Portal/PerformanceReporting/FieldBank/_types';
import FieldBankPanel from './components/FieldBankPanel';
import TemplateSections from './components/TemplateSections';
import FieldDragOverlay from './components/FieldDragOverlay';
import SectionDragOverlay from './components/SectionDragOverlay';
import FieldPropertiesPanel from './components/FieldPropertiesPanel';
import SectionPropertiesPanel from './components/SectionPropertiesPanel';
import TemplateStatusChip from './components/TemplateStatusChip';
import PublishTemplateDialog from './components/PublishTemplateDialog';
import { neutral } from '_lex/_foundations/phxColors';
import { getCorrectNavigationUrl } from './_hooks/useNavigationHandlers';

// Custom collision detection that combines strategies for better drop zone detection
const customCollisionDetection: CollisionDetection = (args) => {
  // First, check for pointer-based collisions with insertable zones
  const pointerCollisions = pointerWithin(args);

  if (pointerCollisions.length > 0) {
    // Filter to only insertable zones (the thin 4px drop zones)
    const insertableZoneCollisions = pointerCollisions.filter((collision) => {
      const container = args.droppableContainers.find((c) => c.id === collision.id);
      const data = container?.data.current;
      return data?.type === 'section-insert-zone' || data?.type === 'section-reorder-zone';
    });

    if (insertableZoneCollisions.length > 0) {
      return insertableZoneCollisions;
    }
  }

  // Fall back to rectangle intersection for other droppables (section drop zones)
  return rectIntersection(args);
};

interface TemplateBuilderProps {
  // Props can be passed to context via provider if needed
}

const TemplateBuilder: React.FC<TemplateBuilderProps> = () => {
  const confirm = useConfirm();
  // Right drawer state for smooth animations
  const [rightDrawerOpen, setRightDrawerOpen] = useState(false);

  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);

  // Get all state and actions from context
  const {
    template,
    templateEditable,
    isLoading,
    isError,
    error,
    isSaving,
    isTemplateDisabled,
    autoSaveError,
    activeId,
    dragData,
    isReorderMode,
    removeFieldFromSection,
    toggleSectionCollapsed,
    addSection,
    removeSection,
    toggleReorderMode,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleBackToTemplateList,
    handleEndEditing,
    sectionIds,
    selectedFieldForEditing,
    setSelectedFieldForEditing,
    updateFieldLabel,
    updateFieldDefaultValues,
    updateFieldRequired,
    updateFieldLocked,
    updateFieldOptions,
    selectedSectionForEditing,
    setSelectedSectionForEditing,
    openFieldOptionsId,
    setOpenFieldOptionsId,
    updateSectionTitle,
    updateSectionDescription,
    updateSectionRequired,
    updateSectionLocked,
    updateSectionRepeatable,
    publishTemplate,
    resetTemplate,
    trackActivity,
    inactiveFieldIds,
    duplicateFieldIds,
    duplicateSectionIds,
    emptySectionIds,
    removableChildFieldIds,
    isLexipolStaff,
  } = useTemplateBuilderContext();

  // Get formatted time elapsed since last save using dayjs
  const timeElapsed = template.updatedAt
    ? dayjs(template.updatedAt).fromNow()
    : t('performanceReporting.templates.builder.labels.neverSaved');

  // Helper function to get error message from autoSaveError
  const getAutoSaveErrorMessage = (error: any): string => {
    if (!error) return '';

    // Check for errorDetails array (API validation errors)
    if (error.errorDetails && Array.isArray(error.errorDetails) && error.errorDetails.length > 0) {
      // Use the first error detail's message, or combine multiple if needed
      const firstError = error.errorDetails[0];
      if (firstError.message) {
        return firstError.message;
      }
    }

    // Check for common error structures
    if (error.errorMessage) return error.errorMessage;
    if (error.message) return error.message;
    if (typeof error === 'string') return error;

    // Fallback for unknown error structures
    return t('performanceReporting.templates.builder.error.autoSaveFailed');
  };

  // Sync drawer open state with content selection
  useEffect(() => {
    if (selectedSectionForEditing || selectedFieldForEditing) {
      setRightDrawerOpen(true);
    }
  }, [selectedSectionForEditing, selectedFieldForEditing]);

  // Close field options panel on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (openFieldOptionsId) {
        setOpenFieldOptionsId(null);
      }
    };

    window.addEventListener('scroll', handleScroll, true); // Use capture to catch all scroll events
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [openFieldOptionsId, setOpenFieldOptionsId]);

  // Close field options panel on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openFieldOptionsId && event.target instanceof Element) {
        // Check if the click is outside of any field options panel
        const isInsideFieldOptions = event.target.closest('[data-field-options-panel]');
        const isInsideField = event.target.closest('[data-field-container]');

        if (!isInsideFieldOptions && !isInsideField) {
          setOpenFieldOptionsId(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openFieldOptionsId, setOpenFieldOptionsId]);

  const handleOpenPublishModal = useCallback(async () => {
    if (isLexipolStaff) {
      try {
        await publishTemplate(undefined, undefined, templateEditable.updatedBy);
      } catch (error) {
        console.error('Failed to publish template:', error);
      }
      return;
    }

    setIsPublishModalOpen(true);
  }, [isLexipolStaff, publishTemplate, templateEditable.updatedBy]);

  const handleClosePublishModal = useCallback(() => {
    setIsPublishModalOpen(false);
  }, []);

  const handleConfirmPublish = useCallback(
    async ({ changeNote, reviewWorkflowId }: { changeNote: string; reviewWorkflowId: number }) => {
      try {
        await publishTemplate(changeNote, reviewWorkflowId, templateEditable.updatedBy);
        setIsPublishModalOpen(false);
      } catch (error) {
        console.error('Failed to publish template:', error);
      }
    },
    [publishTemplate, templateEditable.updatedBy]
  );

  // Handle reset template with confirmation
  const handleResetTemplateClick = useCallback(async () => {
    try {
      await confirm({
        title: t('performanceReporting.templates.details.actions.resetConfirmTitle'),
        description: t('performanceReporting.templates.details.actions.resetConfirmDescription'),
        confirmationText: t('performanceReporting.templates.details.actions.resetConfirmButton'),
        cancellationText: t('common.cancel'),
        confirmationButtonProps: { variant: 'contained', color: 'primary' },
        cancellationButtonProps: { variant: 'outlined', color: 'neutral' },
      });

      await resetTemplate();
    } catch (error) {
      // User cancelled or error occurred
      if (error) {
        console.error('Failed to reset template:', error);
      }
    }
  }, [confirm, resetTemplate]);

  // Handle right drawer close with smooth animation
  const handleSetRightDrawerOpen = useCallback(
    (open: boolean) => {
      setRightDrawerOpen(open);
      if (!open) {
        // Clear selections after animation starts
        setSelectedSectionForEditing(null);
        setSelectedFieldForEditing(null);
      }
    },
    [setSelectedSectionForEditing, setSelectedFieldForEditing]
  );

  // Render the field properties panel
  const renderFieldPropertiesPanel = useCallback(() => {
    if (!selectedFieldForEditing) return null;

    // Find which section contains this field
    // Use hybrid matching: compare by id if saved (id !== 0), otherwise by fieldId
    const currentSection = templateEditable.templateSections.find((section) =>
      section.sectionFields.some((field) =>
        selectedFieldForEditing.id === 0
          ? field.fieldId === selectedFieldForEditing.fieldId && field.id === 0
          : field.id === selectedFieldForEditing.id
      )
    );

    if (!currentSection) return null;

    return (
      <FieldPropertiesPanel
        key={
          selectedFieldForEditing.id === 0
            ? `temp-field-${selectedFieldForEditing.fieldId}`
            : selectedFieldForEditing.id
        }
        field={selectedFieldForEditing}
        section={currentSection}
        parentTemplate={template.parentTemplate}
        currentSection={currentSection}
        onLabelChange={updateFieldLabel}
        onDefaultValueChange={updateFieldDefaultValues}
        onRequiredChange={updateFieldRequired}
        onLockedChange={updateFieldLocked}
        onOptionsChange={updateFieldOptions}
        trackUserActivity={trackActivity}
        isChildTemplate={!!template.parentTemplateId}
      />
    );
  }, [
    selectedFieldForEditing,
    templateEditable.templateSections,
    template.parentTemplate,
    template.parentTemplateId,
    updateFieldLabel,
    updateFieldDefaultValues,
    updateFieldRequired,
    updateFieldLocked,
    updateFieldOptions,
    trackActivity,
  ]);

  // Render the section properties panel
  const renderSectionPropertiesPanel = useCallback(() => {
    if (!selectedSectionForEditing) return null;

    return (
      <SectionPropertiesPanel
        key={
          selectedSectionForEditing.id === 0
            ? `temp-section-${selectedSectionForEditing.sectionGuid}`
            : selectedSectionForEditing.id
        }
        section={selectedSectionForEditing}
        parentTemplate={template.parentTemplate}
        onTitleChange={updateSectionTitle}
        onDescriptionChange={updateSectionDescription}
        onRequiredChange={updateSectionRequired}
        onLockedChange={updateSectionLocked}
        onRepeatableChange={updateSectionRepeatable}
      />
    );
  }, [
    selectedSectionForEditing,
    template.parentTemplate,
    updateSectionTitle,
    updateSectionDescription,
    updateSectionRequired,
    updateSectionLocked,
    updateSectionRepeatable,
  ]);

  const properBackNavigationUrl = getCorrectNavigationUrl(template);

  // Show error state if there's an error loading the template
  if (isError) {
    return (
      <TakeoverLayout
        pageTitle={t('performanceReporting.templates.builder.error.loadingTitle')}
        backUrl={properBackNavigationUrl}
        onBackClick={handleBackToTemplateList}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '50vh',
            gap: 3,
            textAlign: 'center',
          }}
        >
          <Typography
            variant='h5'
            color='error.main'
          >
            {t('performanceReporting.templates.builder.error.failedToLoad')}
          </Typography>
          <Typography
            variant='body1'
            color='text.secondary'
            sx={{ maxWidth: 400 }}
          >
            {t('performanceReporting.templates.builder.error.loadingError')}
          </Typography>
          <Stack
            direction='row'
            spacing={2}
          >
            <Button
              variant='outlined'
              onClick={handleBackToTemplateList}
            >
              {t('performanceReporting.templates.builder.buttons.backToTemplates')}
            </Button>
            <Button
              variant='contained'
              onClick={() => window.location.reload()}
            >
              {t('performanceReporting.templates.builder.buttons.tryAgain')}
            </Button>
          </Stack>
        </Box>
      </TakeoverLayout>
    );
  }

  // Show loading state
  if (isLoading) {
    return (
      <TakeoverLayout
        pageTitle={t('performanceReporting.templates.builder.loading.title')}
        backUrl={properBackNavigationUrl}
        onBackClick={handleBackToTemplateList}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '50vh',
          }}
        >
          <CircularProgress size={60} />
        </Box>
      </TakeoverLayout>
    );
  }

  return (
    <DndContext
      collisionDetection={customCollisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
    >
      <TakeoverLayout
        pageTitle={templateEditable.name}
        backUrl={properBackNavigationUrl}
        onBackClick={handleBackToTemplateList}
        rightDrawerOpen={rightDrawerOpen}
        setRightDrawerOpen={handleSetRightDrawerOpen}
        contentHeaderLeft={
          <Stack
            direction='row'
            spacing={2}
            alignItems='center'
          >
            {template.status === 'Draft' && template.lastPublishDate && (
              <ActionMenu
                id='template-actions-menu'
                btnText={t('performanceReporting.templates.details.actions.resetButton')}
                items={[
                  {
                    key: 'reset',
                    label: t('performanceReporting.templates.details.actions.resetToLastPublished'),
                    onClick: handleResetTemplateClick,
                    disabled: isSaving,
                  },
                ]}
                btnProps={{
                  variant: 'outlined',
                  color: 'primary',
                  size: 'small',
                  disabled: isSaving,
                }}
              />
            )}
          </Stack>
        }
        contentHeaderRight={
          <Stack
            direction='row'
            spacing={2}
            alignItems='center'
          >
            <TemplateStatusChip status={template.status || 'Draft'} />

            <Stack
              direction='row'
              spacing={1}
              alignItems='center'
            >
              <Typography
                variant='body2'
                color='text.secondary'
              >
                {t('performanceReporting.templates.builder.labels.lastSaved')} {timeElapsed}
              </Typography>

              {autoSaveError && (
                <Tooltip
                  title={getAutoSaveErrorMessage(autoSaveError)}
                  placement='bottom'
                  arrow
                >
                  <ErrorOutlineIcon
                    sx={{
                      fontSize: 16,
                      color: '#EF4444', // error[500] equivalent
                      cursor: 'help',
                    }}
                  />
                </Tooltip>
              )}
            </Stack>

            <Button
              variant='outlined'
              color='primary'
              size='small'
              onClick={async () => {
                await handleEndEditing();
              }}
              disabled={isTemplateDisabled || isSaving}
              loading={isTemplateDisabled || isSaving}
              loadingPosition='end'
            >
              {t('performanceReporting.templates.builder.buttons.endEditing')}
            </Button>
            {template.status === 'Draft' && (
              <Button
                variant='contained'
                color='primary'
                size='small'
                onClick={handleOpenPublishModal}
                disabled={isSaving}
              >
                {t('performanceReporting.templates.builder.buttons.publish')}
              </Button>
            )}
          </Stack>
        }
        leftDrawer={{
          header: (
            <Typography
              variant='h6'
              sx={{ fontWeight: 600 }}
            >
              {t('performanceReporting.templates.builder.labels.fieldBank')}
            </Typography>
          ),
          content: <FieldBankPanel />,
          closeable: true,
        }}
        rightDrawer={{
          header: selectedSectionForEditing ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton
                size='small'
                onClick={() => setSelectedSectionForEditing(null)}
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <CloseIcon fontSize='small' />
              </IconButton>
              <Typography
                variant='h6'
                sx={{ fontWeight: 600, flex: 1 }}
              >
                {t('performanceReporting.templates.builder.labels.sectionSettings')}
              </Typography>
            </Box>
          ) : selectedFieldForEditing ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton
                size='small'
                onClick={() => setSelectedFieldForEditing(null)}
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <CloseIcon fontSize='small' />
              </IconButton>
              <Typography
                variant='h6'
                sx={{ fontWeight: 600 }}
              >
                {t('performanceReporting.templates.builder.labels.fieldProperties')}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton
                size='small'
                onClick={() => setRightDrawerOpen(false)}
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <CloseIcon fontSize='small' />
              </IconButton>
              <Typography
                variant='h6'
                sx={{ fontWeight: 600 }}
              >
                {t('performanceReporting.templates.builder.labels.properties')}
              </Typography>
            </Box>
          ),
          content: selectedSectionForEditing ? (
            renderSectionPropertiesPanel()
          ) : selectedFieldForEditing ? (
            renderFieldPropertiesPanel()
          ) : (
            <Box
              sx={{
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                height: '100%',
                minHeight: '400px',
              }}
            >
              <Box
                component='img'
                src='/admin/img/Illus.png'
                alt={t('performanceReporting.templates.builder.labels.noSelection')}
                sx={{
                  width: 160,
                  height: 160,
                  mb: 4,
                  opacity: 0.7,
                }}
              />
              <Typography
                color='text.secondary'
                variant='body2'
                sx={{
                  fontSize: '1.0rem',
                  fontWeight: 400,
                  lineHeight: 1.3,
                  maxWidth: '250px',
                  wordWrap: 'break-word',
                  textAlign: 'center',
                  px: 3,
                  whiteSpace: 'normal',
                }}
              >
                {t('performanceReporting.templates.builder.labels.clickToModify')}
              </Typography>
            </Box>
          ),
          noPadding: false,
          wide: false,
          closeable: true,
          verticalActions: rightDrawerOpen ? undefined : [], // Show arrow when closed, hide when open
        }}
        contentBackground='neutral'
      >
        {!isLexipolStaff && (
          <PublishTemplateDialog
            open={isPublishModalOpen}
            onClose={handleClosePublishModal}
            onConfirm={handleConfirmPublish}
            isSaving={isSaving}
            existingReviewWorkflows={template?.reviewWorkflows}
          />
        )}
        <Box sx={{ position: 'relative' }}>
          {/* Pointer-events blocking overlay */}
          {isTemplateDisabled && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                zIndex: 1000,
                pointerEvents: 'all',
                cursor: 'not-allowed',
              }}
            />
          )}

          <Stack
            spacing={3}
            sx={{
              width: '100%',
              maxWidth: '900px',
              mx: 'auto',
              py: 3,
              px: 2,
              pointerEvents: isTemplateDisabled ? 'none' : 'auto',
            }}
          >
            {/* Template Sections Card */}
            <Card
              sx={{
                border: '1px solid',
                borderColor: neutral[200],
                boxShadow: 'none',
              }}
            >
              <CardHeader
                title={
                  <Stack
                    direction='row'
                    justifyContent='space-between'
                    p={2}
                    alignItems='center'
                  >
                    <Typography variant='h6'>
                      {t('performanceReporting.templates.builder.labels.templateSections')}
                    </Typography>
                    {template.templateSections.filter((s) => !s.isDeleted && !s.effectiveIsDeleted).length >= 2 && (
                      <Button
                        variant='outlined'
                        color='primary'
                        size='small'
                        onClick={async () => {
                          try {
                            await toggleReorderMode();
                          } catch (error) {
                            console.error('Error toggling reorder mode:', error);
                            // Optionally show error message to user
                          }
                        }}
                        disabled={isSaving}
                        loading={isSaving && isReorderMode}
                        loadingPosition='end'
                      >
                        {isReorderMode
                          ? t('performanceReporting.templates.builder.buttons.done')
                          : t('performanceReporting.templates.builder.buttons.reorderSections')}
                      </Button>
                    )}
                  </Stack>
                }
                sx={{
                  py: 1.5,
                  px: 2,
                  backgroundColor: neutral[50],
                  '& .MuiCardHeader-title': {
                    width: '100%',
                  },
                }}
              />
              <Divider />
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                {/* Template Sections Content */}
                <Box sx={{ display: 'grid', gap: 2 }}>
                  <SortableContext
                    items={sectionIds}
                    strategy={verticalListSortingStrategy}
                  >
                    <TemplateSections
                      sections={templateEditable.templateSections}
                      parentTemplate={template.parentTemplate}
                      onRemoveField={removeFieldFromSection}
                      onToggleCollapsed={toggleSectionCollapsed}
                      isReorderMode={isReorderMode}
                      onRemoveSection={removeSection}
                      onSetSelectedField={setSelectedFieldForEditing}
                      onSetSelectedSection={setSelectedSectionForEditing}
                      activeId={activeId}
                      dragData={dragData}
                      openFieldOptionsId={openFieldOptionsId}
                      setOpenFieldOptionsId={setOpenFieldOptionsId}
                      inactiveFieldIds={inactiveFieldIds}
                      duplicateFieldIds={duplicateFieldIds}
                      duplicateSectionIds={duplicateSectionIds}
                      emptySectionIds={emptySectionIds}
                      removableChildFieldIds={removableChildFieldIds}
                      selectedSectionId={selectedSectionForEditing?.id || null}
                      selectedSectionGuid={selectedSectionForEditing?.sectionGuid || null}
                    />
                  </SortableContext>

                  {/* Add Section Button - Compact inline */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 4 }}>
                    <Button
                      variant='outlined'
                      size='small'
                      onClick={() => {
                        addSection();
                      }}
                    >
                      {t('performanceReporting.templates.builder.buttons.addSection')}
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Stack>
        </Box>

        {/* Drag Overlay */}
        <DragOverlay modifiers={[snapCenterToCursor]}>
          {activeId && dragData ? (
            dragData.type === 'field-from-bank' ? (
              <FieldDragOverlay
                field={dragData.field!}
                dragHandlePosition='right'
              />
            ) : dragData.type === 'section' ? (
              <SectionDragOverlay section={dragData.section!} />
            ) : (
              <FieldDragOverlay
                field={
                  {
                    id: dragData.templateField!.fieldId,
                    name: dragData.templateField!.name,
                    label: dragData.templateField!.label,
                    description: dragData.templateField!.description,
                    defaultValues: dragData.templateField!.defaultValues ?? [],
                    fieldType: dragData.templateField!.fieldType,
                    fieldCategoryId: dragData.templateField!.fieldCategoryId,
                    status: dragData.templateField!.fieldStatus,
                    templates: [],
                    createdAt: '',
                    createdBy: '',
                    updatedAt: '',
                    updatedBy: '',
                    fieldCategory: dragData.templateField!.fieldCategory,
                    fieldOptions: dragData.templateField!.sectionFieldOptions || [],
                  } as Field
                }
                dragHandlePosition='left'
              />
            )
          ) : null}
        </DragOverlay>
      </TakeoverLayout>
    </DndContext>
  );
};

export default TemplateBuilder;
